<template>
  <PageHeader :showBreadcrumb="true" />
  <form v-if="(authStore.isAdminUser || authStore.isInvestigatorUser) && appStore.entities.length > 0" @submit.stop.prevent="onSubmit">
    <DataTable
      v-model:editingRows="editingRows"
      editMode="row"
      v-model:filters="filters"
      @rowSelect="onRowSelect"
      :value="adminExternalAppeals"
      ref="adminExternalAppealsTable"
      dataKey="appealId"
      stripedRows
      :loading="loading" p
      aginator :rows="25"
      sortField="createdAt" :sortOrder="-1"
      @rowEditSave="onRowEditSave"
      filterDisplay="row"
      show-gridlines
      :pt="{
        table: { style: 'min-width: 50rem' },
        column: {
          bodycell: ({ state }) => ({
            style: state['d_editing'] && 'padding-top: 0.75rem; padding-bottom: 0.75rem'
          })
        }
      }">
      <template #header>
        <div class="header-components gap-3">
          <Button icon="pi pi-filter-slash" label="Clear Filters" rounded @click="clearFilter()" />
          <IconField>
            <InputText v-model="filters['global'].value" label="Keyword Search" aria-label="Keyword Search" placeholder="Keyword Search" />
            <InputIcon>
              <i class="pi pi-search" ></i>
            </InputIcon>
          </IconField>
          <Button icon="pi pi-external-link" rounded label="Export" @click="exportCSV($event)" />
        </div>
      </template>
      <template #empty> No Data. </template>
      <Column field="internalId" header="Appeal ID" :sortable="true" frozen align-frozen="left">
        <template #filter="{ filterModel, filterCallback }">
          <InputText id="appeal-id-filter" v-model="filterModel.value" type="text" aria-label="Appeal ID Filter" @input="filterCallback()" placeholder="Any" style="width: 6rem" />
        </template>
        <template #body="slotProps">
          <a href="#" @click="onRowSelect(slotProps.data)">{{
              (slotProps.data.internalId !== null) ? slotProps.data.internalId : slotProps.data.appealId
            }}</a>
        </template>
      </Column>
      <Column field="createdAt" header="Appeal Submission Date" :sortable="true" :showFilterMenu="false">
        <template #body="slotProps">
          {{ new Date(slotProps.data.createdAt).toLocaleString() }}
        </template>
      </Column>
      <Column field="healthInsuranceCompany" header="Health Insurance Company" :sortable="true">
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect v-model="filterModel.value" @change="filterCallback()" :options="healthInsuranceCompanies" optionLabel="label" optionValue="value" placeholder="Any" :maxSelectedLabels="1">
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </MultiSelect>
        </template>
        <template #body="slotProps">
          {{ EXTERNAL_APPEALS_HEALTH_INSURANCE_COMPANIES[slotProps.data.healthInsuranceCompany] ?? slotProps.data.healthInsuranceCompany }}
        </template>
      </Column>
      <Column field="patientInsuranceId" header="Patient Insurance ID" :sortable="true" :showFilterMenu="false"/>
      <Column field="typeOfCoverage" header="Coverage Type" :sortable="true">
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect v-model="filterModel.value" @change="filterCallback()" :options="coverageTypes" optionLabel="label" option-value="value" placeholder="Any" :maxSelectedLabels="1">
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </MultiSelect>
        </template>
        <template #body="slotProps">
          {{ EXTERNAL_APPEALS_TYPE_OF_COVERAGES[slotProps.data.typeOfCoverage] ?? slotProps.data.typeOfCoverage }}
        </template>
      </Column>
      <Column field="patientName" header="Patient Name" :sortable="true">
        <template #filter="{ filterModel, filterCallback }">
          <InputText id="patient-name-filter" v-model="filterModel.value" type="text" aria-label="Patient Name Filter" @input="filterCallback()" placeholder="Any" style="width: 6rem" />
        </template>
      </Column>
      <Column field="signature" header="Name of the Person who Submitted the Appeal" :sortable="true" :showFilterMenu="false"/>
      <Column field="relationshipToPatient" header="Relationship to Patient" :sortable="true" :showFilterMenu="false">
        <template #body="slotProps">
          {{ EXTERNAL_APPEALS_PATIENT_RELATIONSHIPS[slotProps.data.relationshipToPatient] ?? slotProps.data.relationshipToPatient ?? EXTERNAL_APPEALS_PATIENT_RELATIONSHIPS.Self }}
        </template>
      </Column>
      <Column field="documents" header="Documents" :sortable="true" :showFilterMenu="false">
        <template #body="slotProps">
          <div class="download-links">
            <span
              v-for="(file, index) in slotProps.data.documents"
              :key="index"
              class="file-link"
            >
              <a
                href="#"
                @click.prevent="downloadFile(file)"
                :aria-label="`Download file: ${file}`"
                :title="`Download ${file}`"
                class="download-link"
              >
                {{ file.replace("externalAppealSupportingDocuments_", "...") }}
                <i class="pi pi-download" aria-hidden="true" style="margin-left: 0.3em;"></i>
              </a>
              <span v-if="index < slotProps.data.documents.length - 1">,&nbsp;</span>
            </span>
          </div>
        </template>
      </Column>
      <Column field="isExpedited" header="Expedited" dataType="boolean" :sortable="true" :showFilterMenu="false">
        <template #filter="{ filterModel, filterCallback }">
          <Checkbox id="expedited-filter" v-model="filterModel.value" aria-label="Expedited Filter" :indeterminate="filterModel.value === null" binary @change="filterCallback()" />
        </template>
        <template #body="slotProps">
          <Tag :value="slotProps.data.isExpedited ? 'Yes' : 'No'" :severity="getTagSeverity(slotProps.data.isExpedited ? 'Yes' : 'No')" />
        </template>
      </Column>
      <Column field="appealStatus" header="Status" :sortable="true">
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect v-model="filterModel.value" @change="filterCallback()" :options="statuses" optionLabel="label" option-value="value" placeholder="Any" :maxSelectedLabels="1">
            <template #option="slotProps">
              <Tag :value="slotProps.option.label" :severity="getTagSeverity(slotProps.option.label)" />
            </template>
          </MultiSelect>
        </template>
        <template #editor="{ data, field }">
          <Select v-model="data[field]" :options="statuses" optionLabel="label" optionValue="value" placeholder="Select Status" fluid>
            <template #option="slotProps">
              <Tag :value="slotProps.option.label" :severity="getTagSeverity(slotProps.option.label)" />
            </template>
          </Select>
        </template>
        <template #body="slotProps">
          <Tag :value="EXTERNAL_APPEALS_STATUSES[slotProps.data.appealStatus] ?? slotProps.data.appealStatus" :severity="getTagSeverity(slotProps.data.appealStatus)" />
        </template>
      </Column>
      <Column field="appealOutcome" header="Appeal Outcome" :sortable="true">
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect v-model="filterModel.value" @change="filterCallback()" :options="outcomes" optionLabel="label" option-value="value" placeholder="Any" :maxSelectedLabels="1">
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </MultiSelect>
        </template>
        <template #editor="{ data, field }">
          <Select v-model="data[field]" :options="outcomes" optionLabel="label" optionValue="value" placeholder="Select Appeal Outcome" fluid>
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
        <template #body="slotProps">
          {{ EXTERNAL_APPEALS_OUTCOMES[slotProps.data.appealOutcome] ?? slotProps.data.appealOutcome }}
        </template>
      </Column>
      <Column field="reviewOrganization" header="Review Organization" :sortable="true">
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect v-model="filterModel.value" @change="filterCallback()" :options="reviewOrganizations" optionLabel="label" option-value="value" placeholder="Any" :maxSelectedLabels="1">
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </MultiSelect>
        </template>
        <template #editor="{ data, field }">
          <Select v-model="data[field]" :options="reviewOrganizations" optionLabel="label" optionValue="value" placeholder="Select Review Organization" fluid>
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
        <template #body="slotProps">
          {{ EXTERNAL_APPEALS_REVIEW_ORGANIZATIONS[slotProps.data.reviewOrganization] ?? slotProps.data.reviewOrganization }}
        </template>
      </Column>
      <Column field="assignedInvestigator" header="Assigned Investigator" :sortable="true">
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect v-model="filterModel.value" @change="filterCallback()" :options="staffUsers" optionLabel="label" option-value="value" placeholder="Any" :maxSelectedLabels="1">
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </MultiSelect>
        </template>
        <template #editor="{ data, field }">
          <Select v-model="data[field]" :options="staffUsers" optionLabel="label" optionValue="value" placeholder="Select Assigned Investigator" fluid>
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
        <template #body="slotProps">
          {{ slotProps.data.assignedInvestigator }}
        </template>
      </Column>
      <Column field="appealCategory" header="Appeal Category" :sortable="true">
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect v-model="filterModel.value" @change="filterCallback()" :options="categories" optionLabel="label" option-value="value" placeholder="Any" :maxSelectedLabels="1">
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </MultiSelect>
        </template>
        <template #editor="{ data, field }">
          <Select v-model="data[field]" :options="categories" optionLabel="label" optionValue="value" placeholder="Select Review Category" fluid>
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
        <template #body="slotProps">
          {{ EXTERNAL_APPEALS_CATEGORIES[slotProps.data.appealCategory] ?? slotProps.data.appealCategory }}
        </template>
      </Column>
      <Column field="iroAt" header="Date sent to IRO" :sortable="true" :showFilterMenu="false">
        <template #editor="{ data, field }">
          <DatePicker v-model="data[field]" fluid showIcon showButtonBar placeholder="MM/DD/YYYY" dateFormat="mm/dd/yy" :maxDate="new Date()" ariaLabel="Date sent to IRO" style="min-width: 10rem" />
        </template>
        <template #body="slotProps">
          {{ slotProps.data.iroAt ? new Date(slotProps.data.iroAt).toLocaleDateString() : '—' }}
        </template>
      </Column>
      <Column field="dueDate" header="Due Date" :sortable="true"/>
      <Column field="upload" header="Upload" :exportable="false">
        <template #body="slotProps">
          <Button
            class="btn btn-primary nowrap-btn"
            label="Upload"
            :aria-label="`Upload document for appeal ${
                  slotProps.data.appealId
            }`"
            icon="pi pi-upload"
            @click="toggleUploadDialog(slotProps.data)"
            rounded
          />
        </template>
      </Column>
      <Column field="notes" header="Notes" :exportable="false">
        <template #body="slotProps">
          <Button
            class="btn btn-success nowrap-btn"
            label="Notes"
            :aria-label="`Notes for appeal ${
              (slotProps.data.internalId!==null)?slotProps.data.internalId:slotProps.data.appealId
            }`"
            icon="pi pi-pen-to-square"
            @click="toggleNotesDialog(slotProps.data)"
            rounded
          />
        </template>
      </Column>
      <Column header="Actions" :rowEditor="true" style="width: 1%; min-width: 8rem" bodyStyle="text-align: center"/>
    </DataTable>
  </form>

  <Dialog
    v-if="appealSelected"
    :visible="isNotesOpen"
    modal
    header="External Appeal Notes"
    :closable="false"
    :style="{ width: '40rem' }"
    @hide="dialogStore.close()"
  >
    <Notes
      :parentType="NOTE_TYPES.Appeal"
      :parentId="(appealSelected.internalId!==null)?appealSelected.internalId:appealSelected.appealId"
      @close="dialogStore.close()"
    />

    <template #footer>
      <div class="d-flex justify-content-end">
        <Button
          type="button"
          rounded
          raised
          label="Close"
          severity="secondary"
          @click="dialogStore.close();"
        />
      </div>
    </template>
  </Dialog>

  <Dialog
    v-if="appealSelected"
    :visible="isUploadOpen"
    modal
    header="Upload Document to External Appeal"
    :closable="false"
    :style="{ width: '40rem' }"
    @hide="closeUploadDialog"
  >
    <p>Upload additional supporting documents for External Appeal #{{
        (appealSelected.internalId!==null)?appealSelected.internalId:appealSelected.appealId
      }}</p>
    <MNFileUpload
      module-type="External Appeal Supporting Documents"
      ref="appealDocumentUpload"
      name="externalAppealSupportingDocuments"
      accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,image/png,image/jpeg,application/msword,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      :fileLimit="20"
    />

    <template #footer>
      <div class="d-flex justify-content-end gap-2">
        <Button
          type="button"
          rounded
          raised
          label="Cancel"
          severity="secondary"
          @click="closeUploadDialog"
        />
        <Button
          type="button"
          rounded
          raised
          label="Save Documents"
          severity="primary"
          @click="saveUploadedDocuments"
          :disabled="uploadInProgress"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import {computed, onMounted, ref} from 'vue';
import PageHeader from "@/components/common/PageHeader.vue";
import {useAppStore} from "@/stores/appStore.js";
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from "primevue/button";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";
import DatePicker from "primevue/datepicker";
import MultiSelect from "primevue/multiselect";
import Checkbox from "primevue/checkbox";
import Tag from "primevue/tag";
import {useAuthStore} from "@/stores/authStore.js";
import {getAdminExternalAppeals, getAllUsers, updateAdminExternalAppeal, addExternalAppealDocument} from "@/services/adminService.js";
import {FilterMatchMode, FilterService} from '@primevue/core/api';
import {getPreSignedDownloadUrl} from "@/services/downloadFile.js";
import {
  EXTERNAL_APPEALS_CATEGORIES, EXTERNAL_APPEALS_HEALTH_INSURANCE_COMPANIES,
  EXTERNAL_APPEALS_OUTCOMES,
  EXTERNAL_APPEALS_PATIENT_RELATIONSHIPS,
  EXTERNAL_APPEALS_REVIEW_ORGANIZATIONS,
  EXTERNAL_APPEALS_STATUSES, EXTERNAL_APPEALS_TYPE_OF_COVERAGES, getAsFormOptions, NOTE_TYPES,
  TOAST_SAVE_LIFE
} from "@/utils/constants.js";
import Select from "primevue/select";
import {AdminExternalAppealModel} from "@/models/AdminExternalAppealModel.js";
import {useToast} from "primevue/usetoast";
import router from "@/router/index.js";
import Notes from "@/components/notes/Notes.vue";
import Dialog from "primevue/dialog";
import {useDialogStore} from "@/stores/dialogStore.js";
import MNFileUpload from "@/components/common/MNFileUpload.vue";

const appStore = useAppStore();
const authStore = useAuthStore();
const dialogStore = useDialogStore();

const isNotesOpen = computed(() => dialogStore.isOpen('externalAppealNotes'));
const isUploadOpen = computed(() => dialogStore.isOpen('externalAppealUpload'));

const toast = useToast();
const adminExternalAppeals = ref([]);
const adminExternalAppealsTable = ref();
const loading = ref(true);
const editingRows = ref([]);
const users = ref([]);
const appealSelected = ref();
const appealDocumentUpload = ref();
const uploadInProgress = ref(false);

const calculateDueDate = (iroDate, isExpedited) => {
  if (!iroDate) return '—';

  const date = new Date(iroDate);
  const daysToAdd = isExpedited ? 3 : 45;

  const dueDate = new Date(date);
  dueDate.setDate(date.getDate() + daysToAdd); // EXCLUDING the IRO date

  return dueDate.toLocaleDateString();
};

const exportCSV = () => {
  adminExternalAppealsTable.value.exportCSV();
};

const onRowSelect = (event) => {
  const model = new AdminExternalAppealModel(event);
  console.log(model);
  router.push({name: 'ViewSaveAsPdfExternalAppeal', query: {
        appealId: model.appealId,
        internalID: model.internalId
  }});
};

const getExternalAppeals = async () => {
  const ea = await getAdminExternalAppeals();
  ea.forEach((e) => {
    if (e.createdAt) {
      e.createdAt = new Date(e.createdAt);
    }
    if (e.iroAt) {
      e.iroAt = new Date(e.iroAt);
    }
    if (e.documents) {
      console.log(`Appeal ${e.appealId} raw documents:`, e.documents);
      e.documents = buildDownloadLinks(e.documents);
      console.log(`Appeal ${e.appealId} parsed documents:`, e.documents);
    }
    e.dueDate = calculateDueDate(e.iroAt, e.isExpedited);
  });
  adminExternalAppeals.value = ea;
};

const getStaffUsers = async () => {
  const u = await getAllUsers();
  users.value = u.filter(user => user.canLogin && user.email.includes("@state.mn.us"));
};

onMounted(() => {
  Promise.all([getExternalAppeals(), getStaffUsers()])
      .then(() => {
        loading.value = false;
      });
});


const downloadFile = async (fileName) => {
  try {
    const responseData = await getPreSignedDownloadUrl(fileName);
    const link = document.createElement('a');
    link.href = responseData.url;
    link.download = fileName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
  } catch (error) {
    console.error('Download failed:', error);
  }
};

const buildDownloadLinks = (downloads) => {
  if (!downloads) return [];
  return downloads.split(',').map(file => file.trim());
};

// Form Select Options lists
const statuses = ref(getAsFormOptions(EXTERNAL_APPEALS_STATUSES));
const outcomes = ref(getAsFormOptions(EXTERNAL_APPEALS_OUTCOMES));
const reviewOrganizations = ref(getAsFormOptions(EXTERNAL_APPEALS_REVIEW_ORGANIZATIONS));
const categories = ref(getAsFormOptions(EXTERNAL_APPEALS_CATEGORIES));
const healthInsuranceCompanies = ref(getAsFormOptions(EXTERNAL_APPEALS_HEALTH_INSURANCE_COMPANIES));
const coverageTypes = ref(getAsFormOptions(EXTERNAL_APPEALS_TYPE_OF_COVERAGES));
const staffUsers = computed(() => {
  return Object
      .entries(users.value)
      .sort(([, a], [, b]) => {
        const lastNameCompare = a.lastName.localeCompare(b.lastName);
        if (lastNameCompare !== 0) {
          return lastNameCompare;
        }
        return a.firstName.localeCompare(b.firstName);
      })
      .map(([, value]) => {
    return {
      label: value.fullName,
      value: value.fullName,
    };
  });
});

const onRowEditSave = async (event) => {
  const {newData} = event;
  const model = new AdminExternalAppealModel(newData);

  delete model.createdAt;
  delete model.updatedAt;
  delete model.documents;
  delete model.dueDate; // calculated field

  try {
    await updateAdminExternalAppeal(model);
    await getExternalAppeals();
    toast.add({ group: 'top-toast', severity: 'success', summary: 'Successfully Updated External Appeal', detail: `External Appeal #${model.appealId}  ${model.internalId} (${model.patientName}) has been updated.`, life: TOAST_SAVE_LIFE });
  } catch (e) {
    toast.add({ group: 'top-toast', severity: 'error', summary: 'Error Updating External Appeal', detail: `External Appeal #${model.appealId} ${model.internalId} (${model.patientName}) could not be updated.\n${e}`, life: TOAST_SAVE_LIFE });
  }
};

const getTagSeverity = (text) => {
  if (text === 'Open') return 'success';
  if (text === 'InfoNeeded' || text === EXTERNAL_APPEALS_STATUSES.InfoNeeded) return 'warn';
  if (text === 'Yes') return 'danger';
  return null;
};

// Filters
FilterService.register('healthInsuranceCompanyFilter', (value, filterSelection) => {
  return filterOtherHandler(value, filterSelection, healthInsuranceCompanies);
});
FilterService.register('typeOfCoverageFilter', (value, filterSelection) => {
  return filterOtherHandler(value, filterSelection, coverageTypes);
});

const filterOtherHandler = (value, filterSelection = [], allOptionsRef) => {
  // No filter selected, show all
  if (value === undefined || filterSelection.length === 0) return true;

  // Extract all known option
  const allOptions = Array.isArray(allOptionsRef?.value)
      ? allOptionsRef.value.map(o => o.value ?? o.label ?? o)
      : [];

  const nonOther = allOptions.filter(v => v !== 'Other');

  const hasOther = filterSelection.includes('Other');
  const regularSelections = filterSelection.filter(v => v !== 'Other');

  // If "Other" is selected, include:
  //   - any row matching a selected regular value
  //   - any row NOT in the known list
  if (hasOther) {
    const isKnown = nonOther.includes(value);
    const matchesRegular = regularSelections.includes(value);
    return matchesRegular || !isKnown;
  }

  // Otherwise, standard inclusion logic
  return filterSelection.includes(value);
};

const filters = ref();
const initFilters = () => {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    appealId: { value: null, matchMode: FilterMatchMode.CONTAINS },
    internalId: { value: null, matchMode: FilterMatchMode.CONTAINS },
    healthInsuranceCompany: { value: null, matchMode: 'healthInsuranceCompanyFilter' },
    typeOfCoverage: { value: null, matchMode: 'typeOfCoverageFilter' },
    patientName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    appealStatus: { value: null, matchMode: FilterMatchMode.IN },
    isExpedited: { value: null, matchMode: FilterMatchMode.EQUALS },
    appealOutcome: { value: null, matchMode: FilterMatchMode.IN },
    reviewOrganization: { value: null, matchMode: FilterMatchMode.IN },
    assignedInvestigator: { value: null, matchMode: FilterMatchMode.IN },
    appealCategory: { value: null, matchMode: FilterMatchMode.IN },
  };
};
initFilters();

const clearFilter = () => {
  initFilters();
};

// Open/close notes dialog
const toggleNotesDialog = async (appeal) => {
  appealSelected.value = appeal;
  dialogStore.toggle('externalAppealNotes');
};

// Open/close upload dialog
const toggleUploadDialog = async (appeal) => {
  appealSelected.value = appeal;
  dialogStore.toggle('externalAppealUpload');
};

const closeUploadDialog = () => {
  if (appealDocumentUpload.value) {
    appealDocumentUpload.value.clear();
  }
  dialogStore.close();
};

const saveUploadedDocuments = async () => {
  if (!appealSelected.value) return;

  try {
    uploadInProgress.value = true;
    const files = await appealDocumentUpload.value?.getUploadedFiles?.();

    console.log('Files from upload component:', files);

    if (!files || files.length === 0) {
      toast.add({
        group: 'top-toast',
        severity: 'warn',
        summary: 'No Files',
        detail: 'Please upload at least one file before saving.',
        life: TOAST_SAVE_LIFE
      });
      return;
    }

    // Save each document to the database
    for (const file of files) {
      console.log('Saving document:', {
        appealId: appealSelected.value.appealId,
        type: file.type,
        name: file.name,
        originalName: file.originalName
      });

      await addExternalAppealDocument(
        appealSelected.value.appealId,
        file.type,
        file.name
      );

      console.log('Document saved successfully:', file.name);
    }

    toast.add({
      group: 'top-toast',
      severity: 'success',
      summary: 'Documents Uploaded',
      detail: `Successfully uploaded ${files.length} document(s) to External Appeal #${appealSelected.value.appealId}`,
      life: TOAST_SAVE_LIFE
    });

    // Refresh the table to show new documents
    console.log('Refreshing appeals list...');
    await getExternalAppeals();
    console.log('Appeals list refreshed');

    // Close dialog and clear upload component
    closeUploadDialog();
  } catch (error) {
    console.error('Upload error:', error);
    toast.add({
      group: 'top-toast',
      severity: 'error',
      summary: 'Upload Failed',
      detail: `Failed to upload documents: ${error.message}`,
      life: TOAST_SAVE_LIFE
    });
  } finally {
    uploadInProgress.value = false;
  }
};
</script>

<style scoped>
.header-components {
  display: flex;
  justify-content: flex-end;
}

.download-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.download-link {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  text-decoration: underline;
  color: var(--primary-color, #007ad9);
}

.download-link:hover {
  color: #005ea3;
}

.download-link i.pi-download {
  font-size: 0.9em;
  margin-left: 0.3em;
}
</style>